import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { teacherPriorityApi, type PriorityRequestListItem } from '@/services/api/teacherPriority'
import { teacherApi } from '@/services/api/teacher'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { RefreshCw } from 'lucide-react'
import { PriorityRequestCard, type PriorityCardExpiry } from '@/components/teacher-request/PriorityRequestCard'
import { StornoModal } from '@/components/teacher-request/StornoModal'
import { StornoRequestsPanel } from '@/components/teacher-request/StornoRequestsPanel'
import { getHighlightParam, clearHighlightParam } from '@/utils/radarNavigation'
import { isPastDate, todayLocalISO } from '@/lib/date-utils'

const PAGE_SIZE = 20

function getExpiryText(expiresAt: string | null): PriorityCardExpiry | null {
  if (!expiresAt) return null
  const expiry = new Date(expiresAt)
  const now = new Date()
  const diffMs = expiry.getTime() - now.getTime()

  if (diffMs <= 0) return { text: 'Abgelaufen', color: 'text-gray-500' }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

  if (diffHours < 2) return { text: `Läuft in ${diffMinutes}m ab`, color: 'text-red-600 font-medium' }
  if (diffHours < 24) return { text: `Läuft in ${diffHours}h ${diffMinutes}m ab`, color: 'text-orange-600' }
  return { text: `Läuft in ${diffHours}h ab`, color: 'text-green-600' }
}

type TimeFilter = 'all' | 'current' | 'past'

const STATUS_CHIPS = [
  { label: 'Nicht storniert', value: '!cancelled' },
  { label: 'Alle', value: '' },
  { label: 'Offen', value: 'pending' },
  { label: 'Teilweise', value: 'partially_accepted,partially_filled' },
  { label: 'Besetzt', value: 'accepted,filled' },
  { label: 'Abgelehnt', value: 'declined' },
  { label: 'Storniert', value: 'cancelled' },
  { label: 'Abgelaufen', value: 'expired' },
]

function getStatusFilterForRequest(request: PriorityRequestListItem): string {
  const status = request.status
  if (status === 'pending') return 'pending'
  if (status === 'partially_filled' || status === 'partially_accepted') return 'partially_accepted,partially_filled'
  if (status === 'accepted' || status === 'filled') return 'accepted,filled'
  if (status === 'declined') return 'declined'
  if (status === 'cancelled') return 'cancelled'
  if (status === 'expired') return 'expired'
  return ''
}

function getTimeFilterForRequest(request: PriorityRequestListItem): TimeFilter {
  if (!request.date) return 'all'
  return isPastDate(request.date) ? 'past' : 'current'
}

function getDateRange(timeFilter: TimeFilter): { dateFrom?: string; dateTo?: string } {
  const todayISO = todayLocalISO()
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayISO = todayLocalISO(yesterday)

  if (timeFilter === 'current') return { dateFrom: todayISO }
  if (timeFilter === 'past') return { dateTo: yesterdayISO }
  return {}
}

export function TeacherRequestsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = searchParams.has('status') ? (searchParams.get('status') || '') : '!cancelled'
  const timeFilter = (searchParams.get('time') as TimeFilter) || 'current'
  const page = parseInt(searchParams.get('page') || '0', 10)
  const [requests, setRequests] = useState<PriorityRequestListItem[]>([])
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [stornoTargetId, setStornoTargetId] = useState<string | null>(null)
  const [_now, setNow] = useState(Date.now())
  const requestsRef = useRef<PriorityRequestListItem[]>([])
  requestsRef.current = requests

  const [highlightedRequestId, setHighlightedRequestId] = useState<string | null>(null)
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const highlightProcessedRef = useRef(false)

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000)
    return () => clearInterval(interval)
  }, [])

  const fetchRequests = async () => {
    setIsLoading(true)
    try {
      const { dateFrom, dateTo } = getDateRange(timeFilter)
      const data = await teacherPriorityApi.listRequests(
        statusFilter || undefined,
        page * PAGE_SIZE,
        PAGE_SIZE,
        dateFrom,
        dateTo
      )
      setRequests(data.requests)
      setTotal(data.total)
      setError(null)
    } catch (e: unknown) {
      setError((e as { detail?: string })?.detail || 'Anfragen konnten nicht geladen werden')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchRequests()
  }, [statusFilter, page, timeFilter])

  const silentRefresh = useCallback(async () => {
    try {
      const { dateFrom, dateTo } = getDateRange(timeFilter)
      const data = await teacherPriorityApi.listRequests(
        statusFilter || undefined,
        page * PAGE_SIZE,
        PAGE_SIZE,
        dateFrom,
        dateTo
      )
      setRequests(prev => {
        if (prev.length === data.requests.length) {
          const prevMap = new Map(prev.map(r => [r.id, r]))
          let changed = false
          const merged = data.requests.map(r => {
            const old = prevMap.get(r.id)
            if (old && JSON.stringify(old) === JSON.stringify(r)) return old
            changed = true
            return r
          })
          if (!changed && prev.length === merged.length) return prev
          return merged
        }
        return data.requests
      })
      setTotal(data.total)
    } catch {
      // silent background refresh
    }
  }, [statusFilter, page, timeFilter])

  // Periodisch (still) neu laden, damit die Antwortfrist der aktuell
  // angefragten Lehrperson aktuell bleibt, sobald eskaliert wird.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      const hasActive = requestsRef.current.some(
        r =>
          !!r.current_answer_deadline ||
          ['pending', 'partially_filled', 'partially_accepted'].includes(r.status)
      )
      if (hasActive) silentRefresh()
    }, 60000)
    return () => clearInterval(id)
  }, [silentRefresh])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        silentRefresh()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [silentRefresh])

  const handleCancel = (id: string, _status: string) => {
    setStornoTargetId(id)
  }

  const handleReRequest = async (requestId: string) => {
    const confirmed = window.confirm(
      'Möchten Sie eine neue Anfrage erstellen? ' +
      'Lehrer die bereits abgelehnt haben, werden erneut gefragt.'
    )
    if (!confirmed) return
    try {
      const result = await teacherPriorityApi.reRequest(requestId)
      setMessage(`Neue Anfrage erstellt: ${result.new_request_id}`)
      await fetchRequests()
    } catch (e: unknown) {
      setError((e as { detail?: string })?.detail || 'Neue Anfrage konnte nicht erstellt werden')
    }
  }

  const [prepResolving, setPrepResolving] = useState<string | null>(null)
  const handleMarkPrepFulfilled = async (requestId: string) => {
    setPrepResolving(requestId)
    try {
      const requests = await teacherPriorityApi.getPrepRequests(requestId)
      for (const req of requests.filter(r => r.status === 'requested')) {
        await teacherPriorityApi.resolvePrepRequest(requestId, req.id, { status: 'fulfilled' })
      }
      await fetchRequests()
    } catch (e: unknown) {
      setError((e as { detail?: string })?.detail || 'Aktion fehlgeschlagen')
    } finally {
      setPrepResolving(null)
    }
  }

  const handleRemoveLesson = async (requestId: string, lessonId: string) => {
    try {
      await teacherPriorityApi.removeLesson(requestId, lessonId)
      await fetchRequests()
      queryClient.invalidateQueries({ queryKey: ['teacher-calendar-month'] })
    } catch (e: unknown) {
      setError((e as { detail?: string })?.detail || 'Lektion konnte nicht entfernt werden')
      throw e
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const displayedRequests = useMemo(() => {
    return [...requests]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .map(r => ({
        ...r,
        expiryInfo: getExpiryText(r.expires_at),
      }))
  }, [requests, _now])

  const setStatusFilter = useCallback((value: string) => {
    setSearchParams({ status: value, time: timeFilter, page: '0' })
  }, [setSearchParams, timeFilter])

  const setTimeFilter = useCallback((filter: TimeFilter) => {
    setSearchParams({ status: statusFilter, time: filter, page: '0' })
  }, [setSearchParams, statusFilter])

  const setPage = useCallback((p: number | ((prev: number) => number)) => {
    const newPage = typeof p === 'function' ? p(page) : p
    setSearchParams({ status: statusFilter, time: timeFilter, page: String(newPage) })
  }, [setSearchParams, statusFilter, timeFilter, page])

  const handleStatusChipChange = (value: string) => {
    setStatusFilter(value)
  }

  const handleTimeFilterChange = (filter: TimeFilter) => {
    setTimeFilter(filter)
  }

  const handleSendReminder = useCallback(async (requestId: string, customMessage?: string) => {
    try {
      const { lesson_request_ids } = await teacherPriorityApi.getRequestLessons(requestId)
      await teacherApi.sendRadarReminder(lesson_request_ids, customMessage)
      setMessage('Erinnerung gesendet')
      await fetchRequests()
    } catch (e: unknown) {
      setError((e as { detail?: string })?.detail || 'Erinnerung konnte nicht gesendet werden')
    }
  }, [fetchRequests])

  const handleSendMessage = useCallback((requestId: string) => {
    navigate(`/teacher/chats?selected=${requestId}`)
  }, [navigate])

  const handleDismissHighlight = useCallback(() => {
    setHighlightedRequestId(null)
    clearHighlightParam()
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current)
    }
  }, [])

  // Request auf anderen Seiten suchen (Pagination-Handling)
  const findRequestPage = useCallback(async (requestId: string) => {
    let pageIndex = 0
    const MAX_SEARCH_PAGES = 20
    while (pageIndex < MAX_SEARCH_PAGES) {
      const { requests: pageRequests, total } = await teacherPriorityApi.listRequests(
        undefined,
        pageIndex * PAGE_SIZE,
        PAGE_SIZE,
        undefined,
        undefined
      )
      const found = pageRequests.find(r => r.id === requestId)
      if (found) return { request: found, page: pageIndex }
      if ((pageIndex + 1) * PAGE_SIZE >= total) break
      pageIndex++
    }
    return null
  }, [])

  useEffect(() => {
    const highlightParam = getHighlightParam()
    
    if (!highlightParam || highlightProcessedRef.current) return
    if (requests.length === 0 || isLoading) return

    // Shared-URL-Detection: 3s statt 6s Cleanup wenn von externem Referrer
    const isShared = !!document.referrer && !document.referrer.includes(window.location.origin)
    const cleanupDelay = isShared ? 3000 : 6000

    const request = requests.find(r => r.id === highlightParam)

    if (request) {
      // Request ist auf der aktuellen Seite sichtbar → Highlight anwenden
      highlightProcessedRef.current = true
      setHighlightedRequestId(highlightParam)

      const cardElement = document.getElementById(`request-card-${highlightParam}`)
      if (cardElement) {
        setTimeout(() => {
          cardElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          })
        }, 100)
      }

      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedRequestId(null)
        clearHighlightParam()
      }, cleanupDelay)
    } else {
      // Request nicht auf aktueller Seite/Filter → suche auf anderen Seiten
      highlightProcessedRef.current = true
      findRequestPage(highlightParam).then(result => {
        if (!result) {
          clearHighlightParam()
          setError('Anfrage nicht gefunden')
          return
        }
        const { request: foundRequest, page: targetPage } = result
        const requiredStatusFilter = getStatusFilterForRequest(foundRequest)
        const requiredTimeFilter = getTimeFilterForRequest(foundRequest)

        if (requiredStatusFilter !== statusFilter || requiredTimeFilter !== timeFilter || targetPage !== page) {
          // Filter/Seite anpassen → Effect läuft nach Reload erneut.
          // WICHTIG: ein einziger setSearchParams-Aufruf — setSearchParams
          // ersetzt den gesamten Query-String und die einzelnen Setter würden
          // sich mit veralteten Closure-Werten gegenseitig überschreiben.
          highlightProcessedRef.current = false
          setSearchParams({ status: requiredStatusFilter, time: requiredTimeFilter, page: String(targetPage) })
        } else {
          // Filter passen bereits (Edge-Case) → direkt anwenden
          setHighlightedRequestId(highlightParam)
          highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedRequestId(null)
            clearHighlightParam()
          }, cleanupDelay)
        }
      })
    }
    
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [requests, isLoading, statusFilter, timeFilter, page, findRequestPage, setStatusFilter, setTimeFilter, setPage, setSearchParams])

  // Browser-Back: Highlight aufräumen wenn URL-Parameter verschwindet
  useEffect(() => {
    const handlePopState = () => {
      const highlightParam = getHighlightParam()
      if (!highlightParam && highlightedRequestId) {
        setHighlightedRequestId(null)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [highlightedRequestId])

  return (
    <ErrorBoundary>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Anfragen von mir</h1>
          <div className="flex gap-2">
            <button
              onClick={fetchRequests}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/teacher/new-request')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
            >
              + Neue Anfrage
            </button>
          </div>
        </div>

        <div className="mb-4">
          <StornoRequestsPanel onResponded={() => fetchRequests()} />
        </div>

        {/* Status chips */}
        <div className="flex gap-2 mb-3 flex-wrap">
          {STATUS_CHIPS.map(chip => (
            <button
              key={chip.value}
              onClick={() => handleStatusChipChange(chip.value)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                statusFilter === chip.value
                  ? 'bg-blue-500 text-white font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Time filter pills */}
        <div className="flex gap-2 mb-4">
          {(['current', 'all', 'past'] as TimeFilter[]).map(filter => (
            <button
              key={filter}
              onClick={() => handleTimeFilterChange(filter)}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                timeFilter === filter
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter === 'all' ? 'Alle' : filter === 'current' ? 'Aktuell' : 'Vergangen'}
            </button>
          ))}
          <span className="text-sm text-gray-500 ml-auto self-center">{total} Anfrage{total !== 1 ? 'n' : ''}</span>
        </div>

        {/* Messages */}
        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded flex justify-between items-center">
            <span>{message}</span>
            <button onClick={() => setMessage(null)} className="text-sm underline">Schliessen</button>
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-sm underline">Schliessen</button>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 border-l-gray-200 dark:border-l-gray-700 p-4 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4" />
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Grid */}
        {!isLoading && (
          <>
            {displayedRequests.length === 0 ? (
              <div className="text-gray-500 text-center py-16">
                Keine Anfragen vorhanden
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {displayedRequests.map(r => (
                  <PriorityRequestCard
                    key={r.id}
                    request={r}
                    expiryInfo={r.expiryInfo}
                    isPrepResolving={prepResolving}
                    onCancel={handleCancel}
                    onMarkPrepFulfilled={handleMarkPrepFulfilled}
                    onReRequest={handleReRequest}
                    onSendReminder={handleSendReminder}
                    onSendMessage={handleSendMessage}
                    onRemoveLesson={r.created_on_behalf ? undefined : (lessonId) => handleRemoveLesson(r.id, lessonId)}
                    onFinanceChanged={fetchRequests}
                    isHighlighted={highlightedRequestId === r.id}
                    isDimmed={highlightedRequestId !== null && highlightedRequestId !== r.id}
                    onDismissHighlight={handleDismissHighlight}
                    onDocumentsChanged={silentRefresh}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-500">
              Seite {page + 1} von {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Zurück
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Weiter
              </button>
            </div>
          </div>
        )}
      </div>

      {stornoTargetId && (
        <StornoModal
          isOpen={true}
          onClose={() => setStornoTargetId(null)}
          requestId={stornoTargetId}
          initiatorRole="requester"
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['teacher-calendar-month'] })
            fetchRequests()
          }}
        />
      )}
    </ErrorBoundary>
  )
}
